import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { Line } from "@ant-design/charts";
import { fmtKRW, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";

/**
 * 자금 예측 라인 차트 — Ant Design Charts(AntV, G2v5).
 * 6개 항목(잔액·입금·출금·누적 매출·누적 지출·지급예정액)을 부드러운 곡선으로 한 그래프에 그린다.
 *   · 색상은 전 시스템 표준(ds.finance) 사용 — 입금 초록 / 출금 빨강 / 잔액 검정 / 지급예정액 주황 ...
 *   · 굵은 라인 위주. 점(마커)은 잔액이 마이너스인 지점에만 빨갛게 찍어 빵꾸를 강조. 범례 마커는 동그라미.
 *   · 0 기준선(회색 점선) 하나를 annotation 으로 그린다(범례엔 안 뜸).
 *
 * ▷ 집계 기준(중요): x축은 "이벤트가 있는 날"이 아니라 규칙적인 구간(버킷)이다.
 *   구간 시작(창 from)부터 단위(일/주/2주/월)로 잘라 그 구간의 입금·출금을 합산해 보여준다.
 *   빈 구간도 직전 잔액을 이월해 선이 끊기지 않는다.
 *
 * ▷ 확대/축소: 우상단 +/− 로 단위를 바꾼다. 최대 확대=하루(일), 이후 주 → 2주 → 월(최대 축소).
 *   날짜 구간([from,to])이 있으면 그 구간 안에서 단위를 바꿔 확대/축소한다.
 *   창을 바꾸면(드래그·기간 변경) 단위는 구간 폭에 맞춰 자동 재설정된다.
 *
 * 인터랙션:
 * - 차트 안을 가로로 8px 이상 드래그 → 그 구간으로 확대(zoom). '구간 초기화'로 복원.
 * - 범례 클릭으로 항목 on/off. 각 지점 값은 호버 툴팁.
 */
const COLORS = {
    "잔액":       ds.finance.balance,
    "입금":       ds.finance.income,
    "출금":       ds.finance.expense,
    "누적 매출":  ds.finance.cumIncome,
    "누적 지출":  ds.finance.cumExpense,
    "지급예정액": ds.finance.scheduledPayment,
};
// 범례·색상 순서
const SERIES = ["잔액", "입금", "출금", "누적 매출", "누적 지출", "지급예정액"];

// x축 날짜 라벨을 짧게 — "2026-04-01" → "26.04.01" (길어서 세로로 눕는 걸 방지)
const shortDate = (s) =>
    (typeof s === "string" && s.length >= 10)
        ? `${s.slice(2, 4)}.${s.slice(5, 7)}.${s.slice(8, 10)}`
        : s;

// ── 확대/축소 단위 — 0(최대 확대)=하루 … 3(최대 축소)=한달 ──────────────
const LEVELS = [
    { key: "day",    label: "일",   days: 1 },
    { key: "week",   label: "주",   days: 7 },
    { key: "biweek", label: "2주",  days: 14 },
    { key: "month",  label: "월",   days: 30 },
];
const MAX_BUCKETS = 70;         // 자동 시작 시 이보다 점이 많으면 한 단계 넓은 단위로
const HARD_MAX_BUCKETS = 400;   // 수동 확대(+)로도 이 이상 촘촘하게는 불가 (점 과다·성능·잘림 방지)

const clamp   = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const pad2    = (n) => String(n).padStart(2, "0");
const parseUTC = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
const toStr    = (ms) => { const d = new Date(ms); return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`; };
const spanDays = (a, b) => Math.max(1, Math.round((parseUTC(b) - parseUTC(a)) / 86400000) + 1);

// 다음 버킷 시작일 — 고정 일수 단위(일/주/2주). 월 단위는 bucketStarts 가 따로 처리.
function addStep(dateStr, key) {
    const days = key === "week" ? 7 : key === "biweek" ? 14 : 1;
    return toStr(parseUTC(dateStr) + days * 86400000);
}

// 창 [winFrom, winTo] 을 단위(key) 버킷 시작일 배열로 — winFrom 을 앵커로 누적 (창 밖 날짜 없음)
function bucketStarts(winFrom, winTo, key) {
    const starts = [];
    if (key === "month") {
        // 월 단위: winFrom 의 '일(day)' 을 고정 앵커로 매달 전진. 월말이면 그 달 마지막 날로만
        // 클램프하되 앵커 일수는 보존한다(01-31 → 02-28 → 03-31 → 04-30, 드리프트 없음).
        const y0 = +winFrom.slice(0, 4), m0 = +winFrom.slice(5, 7), d0 = +winFrom.slice(8, 10);
        for (let k = 0; k < 4000; k++) {
            const y = y0 + Math.floor((m0 - 1 + k) / 12);
            const m = ((m0 - 1 + k) % 12) + 1;
            const dim = new Date(Date.UTC(y, m, 0)).getUTCDate();   // 그 달의 마지막 날
            const s = `${y}-${pad2(m)}-${pad2(Math.min(d0, dim))}`;
            if (s > winTo) break;
            starts.push(s);
        }
        return starts;
    }
    let cur = winFrom, guard = 0;
    while (cur <= winTo && guard++ < 4000) { starts.push(cur); cur = addStep(cur, key); }
    return starts;
}
// dateStr 이 속한 버킷 인덱스 (starts[i] <= dateStr 인 마지막 i) — 이진탐색
function bucketIndexOf(starts, dateStr) {
    let lo = 0, hi = starts.length - 1, ans = 0;
    while (lo <= hi) { const mid = (lo + hi) >> 1; if (starts[mid] <= dateStr) { ans = mid; lo = mid + 1; } else hi = mid - 1; }
    return ans;
}

// 일 단위 원자료(daily) → 창·단위별 버킷 시리즈. 빈 구간은 잔액 이월, 누적은 창 시작 기준.
function buildSeries(daily, winFrom, winTo, key) {
    if (!winFrom || !winTo || winFrom > winTo) return [];
    const { byDate, dates, base } = daily;
    const starts = bucketStarts(winFrom, winTo, key);
    if (!starts.length) return [];

    const agg = starts.map(() => ({ in: 0, out: 0, bal: null }));
    // 창 진입 이월 잔액 = winFrom 이전 마지막 잔액(없으면 기준잔액)
    let carry = base;
    for (const d of dates) if (d < winFrom) carry = byDate.get(d).bal;
    // 창 내 이벤트를 버킷에 합산 (버킷 잔액 = 그 구간 마지막 이벤트의 잔액)
    for (const d of dates) {
        if (d < winFrom || d > winTo) continue;
        const cell = byDate.get(d);
        const i = bucketIndexOf(starts, d);
        agg[i].in += cell.in; agg[i].out += cell.out;
        if (cell.bal != null) agg[i].bal = cell.bal;
    }
    const totalExp = agg.reduce((s, b) => s + b.out, 0);

    const out = [];
    let cumIn = 0, cumOut = 0;
    starts.forEach((s, i) => {
        const b = agg[i];
        cumIn += b.in; cumOut += b.out;
        if (b.bal != null) carry = b.bal;             // 구간 끝 잔액, 없으면 이월
        out.push({ date: s, series: "잔액",       val: carry });
        out.push({ date: s, series: "입금",       val: b.in });
        out.push({ date: s, series: "출금",       val: b.out });
        out.push({ date: s, series: "누적 매출",  val: cumIn });
        out.push({ date: s, series: "누적 지출",  val: cumOut });
        out.push({ date: s, series: "지급예정액", val: totalExp - cumOut });   // 앞으로 나갈 예정 잔여
    });
    return out;
}

export default function CashRunwayChart({ cashflow, from, to }) {
    const [zoom, setZoom]           = useState(null);   // { from, to } | null — 드래그 확대 구간
    const [selBox, setSelBox]       = useState(null);   // 드래그 중 선택 밴드
    const [levelOverride, setLevel] = useState(null);   // null = 자동(구간 폭에 맞춤), 숫자 = 사용자가 +/− 로 고정

    const chartRef    = useRef(null);
    const wrapRef     = useRef(null);
    const dragRef     = useRef(null);
    const viewInfoRef = useRef({ dates: [] });

    // 일 단위 원자료 — 날짜별 입/출/잔액. 잔액은 백엔드 runningBalance(전체 타임라인) 사용.
    const daily = useMemo(() => {
        const rows = (cashflow?.rows || []).filter(r => r.runningBalance != null); // 홀딩·완료 제외
        const base = cashflow?.baseBalance?.amount || 0;
        const baseDate = cashflow?.baseBalance?.baseDate;

        const byDate = new Map();
        if (baseDate) byDate.set(baseDate, { in: 0, out: 0, bal: base });
        rows.forEach(r => {
            const e = byDate.get(r.scheduledDate) || { in: 0, out: 0, bal: null };
            if (r.flowType === "INCOME") e.in += r.amount || 0; else e.out += r.amount || 0;
            e.bal = r.runningBalance;
            byDate.set(r.scheduledDate, e);
        });
        const dates = [...byDate.keys()].sort();
        return {
            byDate, dates, base, nEvents: rows.length,
            dataFrom: dates[0] || null, dataTo: dates[dates.length - 1] || null,
        };
    }, [cashflow]);

    // 표시 창 = 드래그 확대(zoom) > 선택 기간[from,to] > 데이터 전체
    const winFrom = zoom ? zoom.from : (from || daily.dataFrom);
    const winTo   = zoom ? zoom.to   : (to   || daily.dataTo);

    // 자동 단위: 구간 폭을 MAX_BUCKETS 이하로 담는 가장 촘촘한 단위 (없으면 최대 축소=월)
    const autoLevel = useMemo(() => {
        if (!winFrom || !winTo) return 0;
        const s = spanDays(winFrom, winTo);
        const i = LEVELS.findIndex(l => Math.ceil(s / l.days) <= MAX_BUCKETS);
        return i < 0 ? LEVELS.length - 1 : i;
    }, [winFrom, winTo]);
    // 수동 확대 하한 — 창이 너무 넓으면 아주 촘촘한 단위(일/주)는 막는다(점 과다·잘림). 구간을 좁히면 더 촘촘히 가능.
    const minLevel = useMemo(() => {
        if (!winFrom || !winTo) return 0;
        const s = spanDays(winFrom, winTo);
        const i = LEVELS.findIndex(l => Math.ceil(s / l.days) <= HARD_MAX_BUCKETS);
        return i < 0 ? LEVELS.length - 1 : i;
    }, [winFrom, winTo]);
    const levelIdx = clamp(levelOverride ?? autoLevel, minLevel, LEVELS.length - 1);
    const level    = LEVELS[levelIdx];

    // 창이 바뀌면(기간 변경·드래그 확대) 사용자 고정 단위를 풀고 자동으로 되돌린다.
    useEffect(() => { setLevel(null); }, [winFrom, winTo]);

    const viewData  = useMemo(() => buildSeries(daily, winFrom, winTo, level.key), [daily, winFrom, winTo, level.key]);
    const viewDates = useMemo(() => [...new Set(viewData.map(d => d.date))].sort(), [viewData]);
    viewInfoRef.current = { dates: viewDates };

    // y축 도메인 = 실제 데이터 min/max 에 8% 여백 (0 을 억지로 포함하지 않음 → 데이터에 맞춰 자동 스케일).
    // 0 기준선(annotation)은 이 범위 밖이면 자연히 클리핑돼 안 보이고, 잔액이 마이너스로 내려가면 나타난다.
    const yDomain = useMemo(() => {
        let lo = Infinity, hi = -Infinity;
        for (const d of viewData) { if (d.val < lo) lo = d.val; if (d.val > hi) hi = d.val; }
        if (!isFinite(lo) || !isFinite(hi)) return null;
        if (lo === hi) { const p = Math.abs(lo) || 1; return [lo - p, hi + p]; }
        const pad = (hi - lo) * 0.08;
        return [lo - pad, hi + pad];
    }, [viewData]);

    if (daily.nEvents === 0 || viewDates.length < 2) {
        return (
            <Box sx={{ py: 2, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                예정 입출금이 없어 차트를 그릴 수 없습니다.
            </Box>
        );
    }

    const bumpLevel = (delta) => setLevel(clamp(levelIdx + delta, minLevel, LEVELS.length - 1));

    const onReady = (ref) => {
        const chart = ref?.chart || ref;
        if (chart) chartRef.current = chart;
    };

    const applyZoom = (x1, x2) => {
        const d = dragRef.current;
        const dts = viewInfoRef.current.dates;
        if (!d || !dts || dts.length < 3 || !d.plotW) return;
        const frac = (px) => Math.max(0, Math.min(1, (px - d.plotX) / d.plotW));
        const idx  = (px) => Math.round(frac(px) * (dts.length - 1));
        const a = idx(Math.min(x1, x2));
        const b = idx(Math.max(x1, x2));
        if (b - a < 1) return;
        setZoom({ from: dts[a], to: dts[b] });
    };

    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        const wrap = wrapRef.current;
        const chart = chartRef.current;
        if (!wrap || !chart) return;
        const rect = wrap.getBoundingClientRect();
        const o = chart.getCoordinate?.()?.options || {};
        const plotX = o.x ?? 0;
        const plotW = o.width  ?? rect.width;
        const plotY = o.y ?? 0;
        const plotH = o.height ?? Math.max(0, rect.height - 40);
        const clampX = (x) => Math.max(plotX, Math.min(plotX + plotW, x));
        const startX = clampX(e.clientX - rect.left);
        dragRef.current = { startX, rectLeft: rect.left, plotX, plotW, plotY, plotH, moved: false };

        const onMove = (ev) => {
            const d = dragRef.current;
            const x = Math.max(d.plotX, Math.min(d.plotX + d.plotW, ev.clientX - d.rectLeft));
            if (Math.abs(x - d.startX) > 8) d.moved = true;
            if (d.moved) {
                setSelBox({ left: Math.min(d.startX, x), width: Math.abs(x - d.startX), top: d.plotY, height: d.plotH });
            }
        };
        const onUp = (ev) => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            setSelBox(null);
            const d = dragRef.current;
            if (d && d.moved) {
                const endX = Math.max(d.plotX, Math.min(d.plotX + d.plotW, ev.clientX - d.rectLeft));
                applyZoom(d.startX, endX);
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    // 툴팁 제목 — 하루면 그 날, 아니면 그 구간(시작 ~ 끝). 끝은 실제 다음 버킷 직전날(마지막은 창 끝).
    const tooltipTitle = (d) => {
        if (level.key === "day") return d.date;
        const i = viewDates.indexOf(d.date);
        const endStr = (i >= 0 && i < viewDates.length - 1)
            ? toStr(parseUTC(viewDates[i + 1]) - 86400000)
            : winTo;
        return `${d.date} ~ ${endStr}`;
    };

    const config = {
        data: viewData,
        xField: "date",
        yField: "val",
        colorField: "series",
        shapeField: "smooth",   // 부드러운 곡선(예시 이미지처럼)
        height: 300,
        autoFit: true,
        // 그려질 때/데이터가 바뀔 때 라인 드로잉 애니메이션
        animate: { enter: { type: "pathIn", duration: 700 }, update: { duration: 350 } },
        scale: {
            color: { domain: SERIES, range: SERIES.map(s => COLORS[s] || "#94a3b8") },
            ...(yDomain ? { y: { domain: yDomain } } : {}),
        },
        // 굵고 부드러운 선 — 잔액은 가장 굵게, 나머지도 또렷하게
        style: {
            lineWidth: (d) => (d.series === "잔액" ? 4 : 2.6),
            strokeOpacity: (d) => (d.series === "잔액" ? 1 : 0.9),
            lineJoin: "round",
            lineCap: "round",
        },
        // 점(마커)은 잔액이 마이너스인 지점에만 빨간 점으로 강조 (그 외는 반지름 0 → 안 보임)
        point: {
            style: {
                r: (d) => (d.series === "잔액" && d.val < 0 ? 4 : 0),
                fill: ds.finance.expense,
                stroke: "#fff",
                lineWidth: 1.5,
                fillOpacity: 1,
            },
        },
        // 호버 시 나타나는 마커 점은 끈다
        interaction: { tooltip: { marker: false } },
        // 0 기준선 하나 — 범례에 안 뜨는 순수 기준선(빵꾸/마이너스 판독용)
        annotations: [{
            type: "lineY",
            data: [0],
            style: { stroke: ds.finance.baseline, lineWidth: 1, lineDash: [4, 4] },
        }],
        axis: {
            y: { labelFormatter: (v) => fmtKRW(v), title: false },
            // x축 라벨: 월 단위는 YY.MM, 그 외는 YY.MM.DD. 항상 가로(세로로 안 눕게), 겹치면 일부만.
            x: {
                title: false,
                labelFormatter: (v) => (level.key === "month" && typeof v === "string" && v.length >= 7
                    ? `${v.slice(2, 4)}.${v.slice(5, 7)}` : shortDate(v)),
                labelAutoRotate: false,
                labelAutoHide: true,
            },
        },
        // 범례 항목 앞 마커를 동그라미로 (직관적)
        legend: { color: { position: "top", layout: { justifyContent: "center" }, itemMarker: "circle" } },
        tooltip: { title: tooltipTitle, items: [{ channel: "y", valueFormatter: (v) => fmtKRWFull(v) }] },
        onReady,
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                ref={wrapRef}
                onMouseDown={onMouseDown}
                sx={{ position: "relative", width: "100%", userSelect: "none", cursor: "crosshair" }}
            >
                <Line {...config} />

                {/* 드래그 선택 밴드 */}
                {selBox && (
                    <Box sx={{
                        position: "absolute", zIndex: 2, pointerEvents: "none",
                        left: selBox.left, width: selBox.width, top: selBox.top, height: selBox.height,
                        bgcolor: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.55)",
                    }} />
                )}

                {/* 확대/축소 단위 컨트롤 + 구간 초기화 (드래그와 분리) */}
                <Box
                    onMouseDown={e => e.stopPropagation()}
                    sx={{ position: "absolute", top: 2, right: 6, zIndex: 3, display: "flex", alignItems: "center", gap: 0.5 }}
                >
                    <Box sx={{
                        display: "flex", alignItems: "center", height: 24,
                        bgcolor: "#fff", border: "1px solid #e2e8f0", borderRadius: 1,
                    }}>
                        <IconButton size="small" title="확대 (더 촘촘한 단위)"
                            disabled={levelIdx <= minLevel} onClick={() => bumpLevel(-1)}
                            sx={{ p: 0.25, color: "#64748b" }}>
                            <AddRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#475569", px: 0.4, minWidth: 22, textAlign: "center" }}>
                            {level.label}
                        </Typography>
                        <IconButton size="small" title="축소 (더 넓은 단위)"
                            disabled={levelIdx >= LEVELS.length - 1} onClick={() => bumpLevel(+1)}
                            sx={{ p: 0.25, color: "#64748b" }}>
                            <RemoveRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </Box>
                    {zoom && (
                        <Chip
                            label="구간 초기화" size="small"
                            icon={<RestartAltRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setZoom(null)}
                            sx={{
                                height: 24, fontSize: 11.5, cursor: "pointer",
                                bgcolor: "#fff", border: "1px solid #e2e8f0",
                                "& .MuiChip-icon": { color: "#64748b" },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
}
